import * as React from "react";
import { observer } from "mobx-react-lite";
import { TaskViewVisualStore } from "../stores";
import {
    Button,
    Col,
    Dropdown,
    MenuProps,
    Popconfirm,
    Skeleton,
    Tooltip,
} from "antd";
import ActionInputDialog from "./ActionInputDialog";
import { actionRequiresInput } from "./ActionMetadataSelection";

type Props = {
    store: TaskViewVisualStore;
};

const TaskActionControl: React.FC<Props> = ({ store }) => {
    const handleExecuteActionClick = (actionId: string) => {
        const taskAction = store.filteredAvailableTaskActions.find(
            (x) => x.action.id === actionId,
        );

        if (!taskAction || !taskAction.canExecute) {
            return;
        }

        if (
            actionRequiresInput(
                taskAction.action.fields,
                store.metadataDefinitions,
            )
        ) {
            // Display form
            store.setSelectedAction(actionId, undefined);
        } else {
            // Execute action
            store
                .executeAction(
                    store.taskPreview!.id,
                    actionId,
                    {},
                    taskAction.action!.isClientSide,
                    undefined,
                )
                .catch((err) => console.log(err));
        }
    };

    const actionsMenu: MenuProps = {
        items: store.filteredAvailableTaskActions
            .slice(2, store.filteredAvailableTaskActions.length)
            .map((x) => ({
                key: x.action.id,
                label: (
                    <Popconfirm
                        title="Are you sure you want to execute this action?"
                        onConfirm={() => {
                            handleExecuteActionClick(x.action.id);
                        }}
                        placement="topRight"
                        disabled={
                            !x.canExecute || !x.action.isConfirmationRequired
                        }
                    >
                        <Tooltip title={x.reason}>
                            <div
                                style={{ padding: 15 }}
                                onClick={() => {
                                    if (x.action.isConfirmationRequired) {
                                        return;
                                    }
                                    handleExecuteActionClick(x.action.id);
                                }}
                            >
                                {x.action.title ?? x.action.name}
                            </div>
                        </Tooltip>
                    </Popconfirm>
                ),
                disabled: !x.canExecute,
            })),
    };

    return (
        <>
            {store.selectedAction && (
                <ActionInputDialog
                    actionId={store.selectedAction}
                    store={store}
                />
            )}
            {store.availableActionsLoading && (
                <Skeleton.Button active size="default" shape="square" />
            )}
            {store.filteredAvailableTaskActions.slice(0, 2).map((a) => (
                <Col key={a.action.id}>
                    <Popconfirm
                        title="Are you sure you want to execute this action?"
                        onConfirm={() => {
                            handleExecuteActionClick(a.action.id);
                        }}
                        placement="topRight"
                        disabled={
                            !a.canExecute || !a.action.isConfirmationRequired
                        }
                    >
                        <Tooltip title={a.reason}>
                            <Button
                                disabled={!a.canExecute}
                                onClick={() => {
                                    if (a.action.isConfirmationRequired) {
                                        return;
                                    }
                                    handleExecuteActionClick(a.action.id);
                                }}
                            >
                                {a.action.title ?? a.action.name}
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                </Col>
            ))}
            {store.filteredAvailableTaskActions.length > 2 && (
                <Col>
                    <Dropdown
                        menu={actionsMenu}
                        trigger={["click"]}
                        overlayClassName="alpha-dropdown-overlay action-menu"
                    >
                        <Button>...</Button>
                    </Dropdown>
                </Col>
            )}
        </>
    );
};

export default observer(TaskActionControl);
